import React, { FC, PropsWithChildren, useState } from 'react';
import { Link } from 'react-router-dom';
import { GuestShell } from './guest-shell';
import { clearGuestSession, useSessionInfo } from '../session';

/**
 * Guest sessions are restored without Firebase, so the account sign-in page
 * only works once guest mode is cleared and the app reboots into the
 * original Firebase flow. This gate makes that switch explicit.
 */
export const SignInGate: FC<PropsWithChildren> = ({ children }) => {
  const { user, isGuest, isInRoom, name } = useSessionInfo();
  const [busy, setBusy] = useState(false);
  const hasGuestTrace = isGuest || !!user.guestExpired || !!user.guestName;

  if (!hasGuestTrace) return <>{children}</>;

  return (
    <GuestShell title="Sign in">
      <div className="st-card st-stack" data-suit="spades">
        <h1 className="st-h2">Switch to your account?</h1>
        <p className="st-muted">
          {isGuest
            ? `You're playing as the guest “${name}”. Signing in ends guest mode on this device.`
            : 'This device still remembers a guest seat. Signing in will forget it.'}
          {isGuest && isInRoom
            ? ' Leave your current table first if others are waiting on you.'
            : ''}
        </p>
        <button
          type="button"
          className="st-btn st-btn--primary st-btn--block"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            await clearGuestSession();
            // reboot so the original Firebase auth listener is registered
            window.location.assign('/sign-in' + window.location.search);
          }}
        >
          {busy ? 'Switching…' : 'Continue to sign in'}
        </button>
        <Link to="/" className="st-btn st-btn--on-ivory st-btn--block">
          Keep playing as guest
        </Link>
      </div>
    </GuestShell>
  );
};
